import React, { useState } from 'react';
import type { CoinData } from '../types/interfaces';
import { formatCurrency } from '../utils/currency';

interface PriceAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  coin: CoinData;
  onCreateAlert: (
    coinId: string,
    coinSymbol: string,
    coinName: string,
    targetPrice: number,
    direction: 'above' | 'below',
    currency: string,
    emailNotification: boolean
  ) => Promise<any>;
  existingAlerts?: any[];
  currency?: string;
}

export default function PriceAlertModal({
  isOpen,
  onClose,
  coin,
  onCreateAlert,
  existingAlerts = [],
  currency = 'usd'
}: PriceAlertModalProps) {
  const [targetPrice, setTargetPrice] = useState<string>('');
  const [direction, setDirection] = useState<'above' | 'below'>('above');
  const [emailNotification, setEmailNotification] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const currentPrice = coin.current_price;
  const priceNum = parseFloat(targetPrice);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!targetPrice || isNaN(priceNum) || priceNum <= 0) {
      setError('Please enter a valid target price');
      return;
    }

    if (direction === 'above' && priceNum <= currentPrice) {
      setError(`Target price must be above current price (${formatCurrency(currentPrice, currency)})`);
      return;
    }

    if (direction === 'below' && priceNum >= currentPrice) {
      setError(`Target price must be below current price (${formatCurrency(currentPrice, currency)})`);
      return;
    }

    setCreating(true);
    try {
      const result = await onCreateAlert(
        coin.id,
        coin.symbol,
        coin.name,
        priceNum,
        direction,
        currency,
        emailNotification
      );

      if (result) {
        setTargetPrice('');
        setDirection('above');
        setEmailNotification(false);
        onClose();
      } else {
        setError('Connect MetaNet wallet');
      }
    } catch (err) {
      setError('An error occurred while creating the alert');
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-md w-[90%] relative shadow-bubble-lg border-2 border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center z-10"
        >
          &times;
        </button>

        <div className="text-center mb-6">
          <div className="text-5xl mb-3">🔔</div>
          <h2 className="text-2xl mb-2 font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
            Set Price Alert
          </h2>
          <p className="text-text-secondary text-sm">
            {coin.name} ({coin.symbol.toUpperCase()})
          </p>
          <p className="text-text-secondary text-xs mt-2">
            Current Price: <span className="text-primary font-bold">{formatCurrency(currentPrice, currency)}</span>
          </p>
        </div>

        {existingAlerts.length > 0 && (
          <div className="mb-6 p-4 bg-bg rounded-2xl border-2 border-border">
            <p className="text-text-secondary text-sm mb-2 font-bold">Active Alerts:</p>
            <ul className="space-y-2">
              {existingAlerts.map((alert) => (
                <li key={alert.id} className="text-text-secondary text-xs">
                  • {formatCurrency(alert.targetPrice, currency)} {alert.direction === 'above' ? '↑' : '↓'}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block mb-2 text-text-secondary font-bold uppercase tracking-wider text-sm">
              Alert Direction:
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDirection('above')}
                className={`flex-1 px-4 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
                  direction === 'above'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 border-green-500 text-white'
                    : 'bg-bg border-border text-text-secondary hover:border-primary'
                }`}
              >
                ↑ Above
              </button>
              <button
                type="button"
                onClick={() => setDirection('below')}
                className={`flex-1 px-4 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
                  direction === 'below'
                    ? 'bg-gradient-to-r from-red-500 to-pink-500 border-red-500 text-white'
                    : 'bg-bg border-border text-text-secondary hover:border-primary'
                }`}
              >
                ↓ Below
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="targetPrice" className="block mb-2 text-text-secondary font-bold uppercase tracking-wider text-sm">
              Target Price ({currency.toUpperCase()}):
            </label>
            <input
              id="targetPrice"
              type="number"
              step="0.01"
              min="0"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              placeholder={`Enter target price...`}
              className="w-full px-6 py-4 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50"
              required
            />
            <p className="mt-2 text-text-secondary text-xs">
              Alert when price goes {direction === 'above' ? 'above' : 'below'} this amount
            </p>
          </div>

          <div className="flex items-center gap-3 p-4 bg-bg rounded-2xl border-2 border-border">
            <input
              type="checkbox"
              id="emailNotification"
              checked={emailNotification}
              onChange={(e) => setEmailNotification(e.target.checked)}
              className="w-5 h-5 rounded border-2 border-border accent-primary cursor-pointer"
            />
            <label htmlFor="emailNotification" className="flex-1 text-text-secondary text-sm cursor-pointer">
              <span className="font-bold">Email notification</span>
              <span className="ml-2 text-xs text-primary">(+50 satoshis)</span>
            </label>
          </div>

          {error && (
            <div className="p-4 bg-danger/20 border-2 border-danger rounded-2xl text-danger text-sm text-center">
              {error}
            </div>
          )}

          <div className="bg-gradient-to-br from-bg to-bg-secondary p-4 rounded-2xl border-2 border-primary/20">
            <p className="text-text-secondary text-xs text-center mb-2">
              <span className="font-bold">Cost:</span> 100 satoshis {emailNotification && '+ 50 satoshis (email)'}
            </p>
            <p className="text-text-secondary text-xs text-center">
              Alert expires in 30 days. You'll receive a browser notification when triggered.
            </p>
          </div>

          <button
            type="submit"
            disabled={creating}
            className="w-full px-6 py-4 bg-gradient-to-r from-primary to-primary-light border-none rounded-full text-white text-base font-semibold cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-bubble-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? 'Creating Alert...' : '🔔 Create Alert (100 sats)'}
          </button>
        </form>
      </div>
    </div>
  );
}

